// src/layout/builders/ERP/InventoryTab.jsx
import React, { useEffect, useState, useCallback } from "react";
import useERPStore from "../../../store/builders/useERPStore";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import { ITEM_UNIT_MAP, MATERIAL_CATEGORIES } from "../../../lib/materialOptions";
import {
  Plus,
  X,
  Package,
  ArrowLeftRight,
  ArrowLeft,
  XCircle,
  Clock,
  Archive,
  Truck,
  CheckCircle2,
  Eye
} from "lucide-react";

const emptyItemForm = { item_name: "", total_stock: "", unit: "unit" };
const emptyTransferForm = { from_project_id: "", to_project_id: "", item_name: "", quantity: "", unit: "unit", notes: "" };

const STATUS_CFG = {
  accepted: {
    cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: <CheckCircle2 size={12} />,
    label: "Transferred"
  },
  rejected: {
    cls: "bg-rose-100 text-rose-700 border-rose-200",
    icon: <XCircle size={12} />,
    label: "Rejected"
  },
  pending: {
    cls: "bg-amber-100 text-amber-700 border-amber-200",
    icon: <Clock size={12} />,
    label: "Pending Accept"
  },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded font-bold uppercase border ${c.cls}`}>
      {c.icon}
      {c.label}
    </span>
  );
};

const InventoryTab = () => {
  const { inventory, materialTransfers, isLoading, fetchInventory, createInventoryItem, updateInventoryItem, fetchMaterialTransfers, createMaterialTransfer, updateMaterialTransferStatus } = useERPStore();
  const { user } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("stock"); // stock | godown_stock | transfers
  const [showItemModal, setShowItemModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFilter, setTransferFilter] = useState("pending"); // pending | accepted | rejected
  const [expandedGodownItem, setExpandedGodownItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [transferForm, setTransferForm] = useState(emptyTransferForm);
  const [selectedStockProject, setSelectedStockProject] = useState(null);
  const [projectStock, setProjectStock] = useState([]);
  const [projectStockLoading, setProjectStockLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInventory();
    fetchMaterialTransfers();
    if (user?.id) {
      axiosInstancev1.get(`/projects?builderId=${user.id}&status=active&limit=1000&page=1`)
        .then(r => setProjects(r.data?.projects || [])).catch(() => { });
    }
  }, [fetchInventory, fetchMaterialTransfers, user?.id]);

  const openItemModal = (item = null) => {
    setEditingItem(item);
    setItemForm(item ? { item_name: item.item_name, total_stock: item.total_stock, unit: item.unit } : emptyItemForm);
    setShowItemModal(true);
  };

  const handleItemSave = async (e) => {
    e.preventDefault(); setSaving(true);
    let ok = editingItem ? await updateInventoryItem(editingItem.id, itemForm) : await createInventoryItem(itemForm);
    setSaving(false);
    if (ok) {
      setShowItemModal(false);
      setItemForm(emptyItemForm);
      setEditingItem(null);
    }
  };

  const handleTransferSave = async (e) => {
    e.preventDefault();

    // Use the same stock source shown in the modal UI (fromItems)
    const selectedFromItem = fromItems.find(i => i.item_name === transferForm.item_name);
    let availableStock = parseFloat(selectedFromItem?.available_stock || 0);

    // Fallback to item-availability API if fromItems not yet loaded
    if (!selectedFromItem) {
      const fromId = transferForm.from_project_id === "" ? 0 : parseInt(transferForm.from_project_id, 10);
      const availability = itemAvailability.find(a => Number(a.project_id) === fromId);
      availableStock = parseFloat(availability?.available_stock || 0);
    }

    const requestedQty = parseFloat(transferForm.quantity || 0);
    if (requestedQty > availableStock) {
      toast.error(`Only ${availableStock} ${transferForm.unit} available in source.`);
      return;
    }

    setSaving(true);
    const ok = await createMaterialTransfer({
      ...transferForm,
      is_direct: transferForm.from_project_id === ""
    });
    setSaving(false);
    if (ok) {
      setShowTransferModal(false);
      setTransferForm(emptyTransferForm);
    }
  };

  const handleTransferAction = async (id, action) => {
    await updateMaterialTransferStatus(id, action);
  };

  const pendingTransfers = materialTransfers.filter(t => t.status === "pending").length;
  const [itemAvailability, setItemAvailability] = useState([]);
  const [fromItems, setFromItems] = useState([]);

  const openStockProject = useCallback(async (project) => {
    setSelectedStockProject(project);
    setProjectStockLoading(true);
    try {
      const res = await axiosInstancev1.get(`/erp/inventory/project-status/${project.id}`, {
        params: { builderId: user?.id }
      });
      const items = (res.data?.data || []).map((i) => {
        const available =
          parseFloat(i.purchased_for_project || 0) +
          parseFloat(i.transferred_in || 0) -
          parseFloat(i.transferred_out || 0) -
          parseFloat(i.used_in_project || 0);
        return { ...i, available_stock: available };
      }).filter((i) =>
        parseFloat(i.purchased_for_project || 0) > 0 ||
        parseFloat(i.transferred_in || 0) > 0 ||
        parseFloat(i.used_in_project || 0) > 0 ||
        parseFloat(i.transferred_out || 0) > 0
      );
      setProjectStock(items);
    } catch (err) {
      console.error("Error fetching project stock:", err);
      setProjectStock([]);
      toast.error("Failed to load project stock");
    } finally {
      setProjectStockLoading(false);
    }
  }, [user?.id]);

  // Clear project selection when leaving stock view
  useEffect(() => {
    if (view !== "stock") setSelectedStockProject(null);
  }, [view]);

  const fetchItemAvailability = useCallback(async (itemName) => {
    if (!itemName) return;
    try {
      const res = await axiosInstancev1.get(`/erp/inventory/item-availability`, {
        params: { itemName, builderId: user?.id }
      });
      setItemAvailability(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching item availability:", err);
      setItemAvailability([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (showTransferModal && transferForm.item_name) {
      fetchItemAvailability(transferForm.item_name);
    }
  }, [showTransferModal, transferForm.item_name, fetchItemAvailability]);

  // Fetch available items for the source project
  useEffect(() => {
    if (!showTransferModal) {
      setFromItems([]);
      return;
    }

    if (transferForm.from_project_id === "") {
      // Godown
      const available = inventory
        .map(i => ({ 
          ...i, 
          available_stock: parseFloat(i.godown_stock || 0) 
        }))
        .filter(i => i.available_stock > 0);
      setFromItems(available);
    } else {
      // Project
      const fetchProjectItems = async () => {
        try {
          const res = await axiosInstancev1.get(`/erp/inventory/project-status/${transferForm.from_project_id}`, {
            params: { builderId: user?.id }
          });
          const items = res.data?.data || [];
          const available = items.map(i => {
            const stock = parseFloat(i.purchased_for_project || 0) + parseFloat(i.transferred_in || 0) - parseFloat(i.transferred_out || 0) - parseFloat(i.used_in_project || 0);
            return { ...i, available_stock: stock };
          }).filter(i => i.available_stock > 0);
          setFromItems(available);
        } catch (err) {
          console.error("Error fetching project stock:", err);
          setFromItems([]);
        }
      };
      fetchProjectItems();
    }
  }, [showTransferModal, transferForm.from_project_id, inventory, user?.id]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setView("stock"); setSelectedStockProject(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'stock' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            Stock in Project
          </button>
          <button onClick={() => setView("godown_stock")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'godown_stock' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            Godown Stock
          </button>
          <button onClick={() => setView("transfers")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'transfers' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            Transfer Stock {pendingTransfers > 0 && `(${pendingTransfers})`}
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowTransferModal(true)}
            className="flex items-center gap-2 bg-white text-gray-700 border border-gray-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition shadow-sm">
            <ArrowLeftRight size={16} /> Direct Transfer Stock
          </button>
          <button onClick={() => openItemModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm">
            <Plus size={16} /> Add Stock
          </button>
        </div>
      </div>

      {
        isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-purple-600"></div>
            <p className="text-sm font-medium text-slate-500">Indexing material databases...</p>
          </div>
        ) : (
          <div className="space-y-6 animate-in fade-in duration-500">
            {view === "stock" && (
              <div className="space-y-4">
                {selectedStockProject ? (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="flex items-center gap-3 p-4 sm:p-5 border-b bg-gray-50/80">
                      <button
                        onClick={() => { setSelectedStockProject(null); setProjectStock([]); }}
                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors bg-white border border-gray-200 rounded-lg shadow-sm shrink-0"
                        title="Back to projects"
                      >
                        <ArrowLeft size={18} />
                      </button>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 font-medium">Stock in Project</p>
                        <h3 className="text-lg font-bold text-gray-800 truncate">
                          {selectedStockProject.project_name}
                        </h3>
                      </div>
                    </div>

                    {projectStockLoading ? (
                      <div className="py-16 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600 mx-auto"></div>
                      </div>
                    ) : projectStock.length === 0 ? (
                      <div className="py-16 text-center flex flex-col items-center gap-3">
                        <Package size={32} className="text-gray-200" />
                        <p className="text-gray-400 font-medium text-sm">No stock recorded for this project.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                              <th className="text-left px-4 py-3">Item Name</th>
                              <th className="text-center px-4 py-3">Purchased</th>
                              <th className="text-center px-4 py-3">Transferred In</th>
                              <th className="text-center px-4 py-3">Transferred Out</th>
                              <th className="text-center px-4 py-3">Used</th>
                              <th className="text-center px-4 py-3">Available</th>
                              <th className="text-center px-4 py-3">Unit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {projectStock.map((item) => (
                              <tr key={item.id || item.item_name} className="hover:bg-gray-50/30 transition-colors">
                                <td className="px-4 py-4">
                                  <span className="font-bold text-gray-800">{item.item_name}</span>
                                </td>
                                <td className="px-4 py-4 text-center font-medium text-gray-700 tabular-nums">
                                  {parseFloat(item.purchased_for_project || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-4 text-center font-medium text-emerald-600 tabular-nums">
                                  {parseFloat(item.transferred_in || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-4 text-center font-medium text-orange-600 tabular-nums">
                                  {parseFloat(item.transferred_out || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-4 text-center font-medium text-rose-600 tabular-nums">
                                  {parseFloat(item.used_in_project || 0).toLocaleString()}
                                </td>
                                <td className={`px-4 py-4 text-center font-bold tabular-nums ${item.available_stock < 0 ? "text-rose-600" : "text-blue-600"}`}>
                                  {parseFloat(item.available_stock || 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-4 text-center text-gray-400 font-medium text-[10px] uppercase tracking-wider">{item.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : projects.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-20 text-center flex flex-col items-center gap-4">
                    <Package size={40} className="text-slate-200" />
                    <p className="text-slate-500 font-medium">No active projects found.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b bg-gray-50/50">
                      <h3 className="text-lg font-bold text-gray-800">Select a Project</h3>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">Click a project to view its stock</p>
                    </div>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                          <th className="text-left px-4 py-3">Project Name</th>
                          <th className="text-center px-4 py-3">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {projects.map((pr) => (
                          <tr
                            key={pr.id}
                            onClick={() => openStockProject(pr)}
                            className="hover:bg-blue-50/40 transition-colors group cursor-pointer"
                          >
                            <td className="px-4 py-4">
                              <span className="font-bold text-gray-800 group-hover:text-blue-700">
                                {pr.project_name}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openStockProject(pr); }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all font-semibold text-xs border border-blue-100 shadow-sm"
                                >
                                  <Eye size={14} /> View Stock
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {view === "godown_stock" && (
              <div className="space-y-4">
                {inventory.filter((item) => (parseFloat(item.godown_stock) || 0) > 0).length === 0 ? (
                  <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-20 text-center flex flex-col items-center gap-4">
                    <Archive size={40} className="text-slate-200" />
                    <p className="text-slate-500 font-medium text-sm">No godown stock available.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b bg-gray-50/50">
                      <h3 className="text-lg font-bold text-gray-800">Godown Stock</h3>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">Central warehouse inventory</p>
                    </div>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                          <th className="text-left px-4 py-3">Item Name</th>
                          <th className="text-center px-4 py-3">Godown Stock</th>
                          <th className="text-center px-4 py-3">Unit</th>
                          <th className="text-center px-4 py-3">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {inventory.filter((item) => (parseFloat(item.godown_stock) || 0) > 0).map((item) => {
                          const isExpanded = expandedGodownItem === item.item_name;
                          return (
                          <React.Fragment key={item.id}>
                            <tr className="hover:bg-gray-50/30 transition-colors">
                              <td className="px-4 py-4">
                                <span className="font-bold text-gray-800">{item.item_name}</span>
                              </td>
                              <td className="px-4 py-4 text-center font-bold text-blue-600 tabular-nums">
                                {parseFloat(item.godown_stock || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-4 text-center text-gray-400 font-medium text-[10px] uppercase tracking-wider">{item.unit}</td>
                              <td className="px-4 py-4">
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => setExpandedGodownItem(isExpanded ? null : item.item_name)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all font-semibold text-xs border shadow-sm ${
                                      isExpanded 
                                        ? "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200" 
                                        : "bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-600 hover:text-white"
                                    }`}
                                  >
                                    <Eye size={14} /> {isExpanded ? "Hide" : "View"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-blue-50/20">
                                <td colSpan={4} className="p-0 border-b border-gray-100">
                                  <div className="p-4 px-6 border-l-2 border-blue-400">
                                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
                                      <ArrowLeftRight size={12} /> Transfer History from Godown
                                    </h4>
                                    {materialTransfers.filter(t => t.item_name === item.item_name && (!t.from_project_id || String(t.from_project_id) === "0")).length === 0 ? (
                                      <p className="text-xs text-gray-400 font-medium py-2">No transfer history found for this item from Godown.</p>
                                    ) : (
                                      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                        <table className="w-full text-xs text-left">
                                          <thead className="bg-gray-50 text-gray-500">
                                            <tr>
                                              <th className="px-3 py-2 font-semibold">Date</th>
                                              <th className="px-3 py-2 font-semibold">To Project</th>
                                              <th className="px-3 py-2 font-semibold">Quantity</th>
                                              <th className="px-3 py-2 font-semibold">Status</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100">
                                            {materialTransfers
                                              .filter(t => t.item_name === item.item_name && (!t.from_project_id || String(t.from_project_id) === "0"))
                                              .map(t => (
                                              <tr key={t.id} className="hover:bg-gray-50/50">
                                                <td className="px-3 py-2 font-medium text-gray-500">{new Date(t.requested_at).toLocaleDateString('en-IN')}</td>
                                                <td className="px-3 py-2 font-semibold text-blue-600">{t.to_project_name}</td>
                                                <td className="px-3 py-2 font-bold text-gray-700">{parseFloat(t.quantity).toLocaleString()} {t.unit}</td>
                                                <td className="px-3 py-2">
                                                  <StatusBadge status={t.status} />
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )})}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {view === "transfers" && (
              <div className="space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-4 border-b bg-gray-50/50">
                    <h3 className="text-lg font-bold text-gray-800">Transfer Stock</h3>
                    <p className="text-xs text-gray-500 font-medium mt-0.5">
                      Godown transfers from this page are applied instantly. Project transfers need Accept or Reject.
                    </p>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 flex items-center gap-1.5">
                    {[
                      { key: "pending", label: "New Request" },
                      { key: "accepted", label: "Accepted" },
                      { key: "rejected", label: "Rejected" },
                    ].map(f => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setTransferFilter(f.key)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition ${
                          transferFilter === f.key
                            ? f.key === "accepted" ? "bg-emerald-500 text-white border-emerald-500"
                              : f.key === "rejected" ? "bg-red-500 text-white border-red-500"
                              : "bg-amber-500 text-white border-amber-500"
                            : f.key === "accepted" ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                              : f.key === "rejected" ? "bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                              : "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100"
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {materialTransfers.filter(t => t.status === transferFilter).length === 0 ? (
                  <div className="col-span-full bg-white rounded-lg border border-dashed border-gray-200 p-16 text-center flex flex-col items-center gap-3">
                    <Truck size={32} className="text-gray-200" />
                    <p className="text-gray-400 font-medium text-sm">No transfer stock requests match this filter.</p>
                  </div>
                ) : materialTransfers.filter(t => t.status === transferFilter).map(t => {
                  const needsReview = t.status === "pending";
                  return (
                  <div key={t.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800">{t.item_name}</span>
                          <span className="text-xs text-gray-500 font-medium">× {parseFloat(t.quantity).toLocaleString()} {t.unit}</span>
                        </div>
                        <StatusBadge status={t.status} />
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100 text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[9px] font-semibold text-gray-400 uppercase">From</span>
                          <span className="font-semibold text-gray-700 truncate max-w-[80px]">{t.from_project_name || "Godown"}</span>
                        </div>
                        <ArrowLeftRight size={14} className="text-gray-300" />
                        <div className="flex flex-col gap-0.5 text-right">
                          <span className="text-[9px] font-semibold text-gray-400 uppercase">To</span>
                          <span className="font-semibold text-blue-600 truncate max-w-[80px]">{t.to_project_name}</span>
                        </div>
                      </div>

                      {t.notes && <p className="text-[11px] text-gray-500 italic bg-gray-50/50 p-2 rounded border border-gray-100">{t.notes}</p>}

                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-gray-400 font-medium">{new Date(t.requested_at).toLocaleDateString('en-IN')}</span>
                        {needsReview && (
                          <div className="flex gap-2">
                            <button onClick={() => handleTransferAction(t.id, "accepted")} className="px-2.5 py-1 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg hover:bg-emerald-600 hover:text-white transition-colors" title="Accept">Accept</button>
                            <button onClick={() => handleTransferAction(t.id, "rejected")} className="px-2.5 py-1 text-[10px] font-bold uppercase bg-rose-50 text-rose-600 border border-rose-100 rounded-lg hover:bg-rose-600 hover:text-white transition-colors" title="Reject">Reject</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
              </div>
            )
            }
          </div >
        )
      }

      {/* Modals - Simplified */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">{editingItem ? "Edit Stock" : "Add Stock to Godown"}</h3>
              <button onClick={() => setShowItemModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleItemSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Item Name *</label>
                <select required value={itemForm.item_name} onChange={e => {
                  const val = e.target.value;
                  const unit = ITEM_UNIT_MAP[val];
                  setItemForm(p => ({ ...p, item_name: val, unit: unit || p.unit }));
                }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors bg-white">
                  <option value="" disabled>Select material...</option>
                  {itemForm.item_name && !ITEM_UNIT_MAP[itemForm.item_name] && (
                    <option value={itemForm.item_name}>{itemForm.item_name} (Custom)</option>
                  )}
                  {MATERIAL_CATEGORIES.map(cat => (
                    <optgroup key={cat.category} label={cat.category}>
                      {cat.items.map(item => <option key={item} value={item}>{item}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Quantity *</label>
                  <input required type="number" min="0.01" step="0.01" value={itemForm.total_stock} onChange={e => setItemForm(p => ({ ...p, total_stock: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Unit</label>
                  <input value={itemForm.unit} onChange={e => setItemForm(p => ({ ...p, unit: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" placeholder="pcs/kg" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowItemModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? "Saving..." : editingItem ? "Update Stock" : "Add Stock"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">Direct Transfer Stock</h3>
              <button onClick={() => setShowTransferModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleTransferSave} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">From</label>
                  <select value={transferForm.from_project_id} onChange={e => setTransferForm(p => ({ ...p, from_project_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none bg-white">
                    <option value="">CENTRAL GODOWN</option>
                    {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">To *</label>
                  <select required value={transferForm.to_project_id} onChange={e => setTransferForm(p => ({ ...p, to_project_id: e.target.value }))} className="w-full border border-blue-100 bg-blue-50/30 rounded-lg px-3 py-2 text-sm outline-none font-semibold text-blue-700">
                    <option value="">Select Target</option>
                    {projects.filter(pr => String(pr.id) !== String(transferForm.from_project_id)).map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Material Item *</label>
                <select required value={transferForm.item_name} onChange={e => {
                  const v = e.target.value;
                  const item = fromItems.find(i => i.item_name === v) || inventory.find(i => i.item_name === v);
                  setTransferForm(p => ({ ...p, item_name: v, unit: item?.unit || p.unit }));
                }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
                  <option value="">Select Material</option>
                  {fromItems.map(i => (
                    <option key={i.id || i.item_name} value={i.item_name}>
                      {i.item_name} [Avl: {i.available_stock} {i.unit}]
                    </option>
                  ))}
                  {fromItems.length === 0 && transferForm.from_project_id !== "" && (
                    <option disabled>No items in stock for this source</option>
                  )}
                </select>
                {transferForm.item_name && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-600 text-white shadow-sm animate-in zoom-in-95 duration-200">
                      Available: {fromItems.find(a => a.item_name === transferForm.item_name)?.available_stock || 0} {transferForm.unit}
                    </span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Quantity *</label>
                  <input required type="number" min="0.01" step="0.01" value={transferForm.quantity} onChange={e => setTransferForm(p => ({ ...p, quantity: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Unit</label>
                  <input value={transferForm.unit} onChange={e => setTransferForm(p => ({ ...p, unit: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" placeholder="pcs/kg" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Logistics Note</label>
                <input value={transferForm.notes} onChange={e => setTransferForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" placeholder="Reason for transfer" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowTransferModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {saving ? "Transferring..." : transferForm.from_project_id === "" ? "Transfer Now" : "Submit for Accept"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default InventoryTab;
